import { IndicatorType } from '@melodic-ui/types';
import { format } from 'date-fns';
import { ILessonListItem } from '@lb/api/mylb/interfaces';
import { LESSON_STATUS_CONFIG } from './lesson-status-config';
import { isLessonEditable } from './lesson-helper.util';

export type LessonTagView = 'learner' | 'instructor' | 'history';

export interface LessonTag {
	text: string;
	icon?: string;
	type?: IndicatorType;
}

export interface LessonTagSummary {
	tags: LessonTag[];
	hasWarning: boolean;
	hasError: boolean;
}

interface BuildLessonTagsOptions {
	lesson: ILessonListItem;
	view: LessonTagView;
	referenceTime?: Date;
}

/**
 * Generates lesson status tags and derived summary flags that are shared between
 * lesson card and popup presentations.
 */
export function buildLessonTags({ lesson, view, referenceTime = new Date() }: BuildLessonTagsOptions): LessonTagSummary {
	const tags: LessonTag[] = [];

	const userSpecificTags: Record<LessonTagView, () => void> = {
		learner: () => {
			if (lesson.lessonStatusID === 3 && !lesson.makeUp) {
				tags.push({ text: `Instructor's Request` });
			}
		},
		instructor: () => {
			const lessonStartDate = new Date(lesson.lessonDate);

			// If lesson is not pending reschedule or awaiting payment, and the lesson start date has passed without attendance marked.
			// Not on a lesson from another instructor's era: the viewer can't record it, so it isn't theirs to fix.
			if (
				isLessonEditable(lesson) &&
				lesson.lessonStatusID !== 3 &&
				lesson.lessonStatusID !== 4 &&
				lessonStartDate < referenceTime &&
				!lesson.attendanceStatusID
			) {
				tags.push({
					text: LESSON_STATUS_CONFIG['missing-attendance'].label,
					type: LESSON_STATUS_CONFIG['missing-attendance'].indicatorType,
					icon: LESSON_STATUS_CONFIG['missing-attendance'].icon
				});
			}
		},
		history: () => {
			// History view is read-only and does not surface dynamic tags today.
		}
	};

	userSpecificTags[view]?.();

	tags.push(getLessonStatusTag(lesson));

	if (lesson.lessonSubstatusID != null) {
		tags.push({ text: lesson.lessonSubstatusName || 'Substatus Unknown', type: 'warning' });
	}

	if (lesson.makeUp) {
		tags.push({ text: 'Makeup Lesson' });
	}

	const hasWarning = tags.some((tag) => tag.type === 'warning') || lesson.attendanceStatusID === 2; // Consider "Absent" attendance status as a warning
	const hasError = tags.some((tag) => tag.type === 'error');

	return {
		tags,
		hasWarning,
		hasError
	};
}

function getLessonStatusTag(lesson: ILessonListItem): LessonTag {
	switch (lesson.lessonStatusID) {
		case 1:
			return {
				text: LESSON_STATUS_CONFIG['scheduled'].label,
				type: LESSON_STATUS_CONFIG['scheduled'].indicatorType,
				icon: LESSON_STATUS_CONFIG['scheduled'].icon
			};
		case 2: {
			const originalDate = lesson.originalLessonDate ? new Date(lesson.originalLessonDate) : null;
			if (originalDate) {
				return { text: 'Rescheduled from ' + format(originalDate, 'MMM d') };
			}
			return { text: 'Rescheduled' };
		}
		case 3:
			return {
				text: LESSON_STATUS_CONFIG['reschedule'].label,
				type: LESSON_STATUS_CONFIG['reschedule'].indicatorType,
				icon: LESSON_STATUS_CONFIG['reschedule'].icon
			};
		case 4:
			return {
				text: LESSON_STATUS_CONFIG['awaiting-payment'].label,
				type: LESSON_STATUS_CONFIG['awaiting-payment'].indicatorType,
				icon: LESSON_STATUS_CONFIG['awaiting-payment'].icon
			};
		case 5:
			return {
				text: LESSON_STATUS_CONFIG['completed'].label,
				type: LESSON_STATUS_CONFIG['completed'].indicatorType,
				icon: LESSON_STATUS_CONFIG['completed'].icon
			};
		case 6:
			return { text: 'Unverified' };
		case 7:
			return { text: 'Cancelled' };
		default:
			return { text: 'Unknown Status', type: 'error' };
	}
}
